import { JoistPlacement } from "building-types/floors/floorJoistPlacementTypes";
import type { Framing } from "building-types/framing/framingTypes";
import type { LumberPiecesByProfile, LumberRail } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType, type RoofFramingComponent } from "building-types/framing/roofFramingComponentTypes";
import { makeFramingProfile2D, makeLumberProfile } from "doc/utils/lumberTools";
import type { BRep, Face } from "geom/brep";
import { clipFacesWithPlanes } from "geom/bspClipping";
import { extrudeFaceAlongPolyLine3 } from "geom/extrude";
import { polyLine3Copy, polyLine3ExtendBothEnds, polyLine3ExtendEnd, polyLine3ExtendStart } from "geom/polyLine3";

export interface FloorFramingExtrusionPiece {
  readonly brep: BRep;
  readonly joistPlacement: JoistPlacement;
  readonly rail: LumberRail;
}

/**
 * Extrude legacy `Framing[]` from the room-class floor system (`SolidFramingFloor.floorFraming`),
 * tagging each solid with its {@link JoistPlacement} so rendering can apply per-type materials
 * (rim vs floor joist, etc.) consistently with framed-floor-assembly geometry.
 */
export function extrudeFloorFramingPlacements(framing: Framing[]): FloorFramingExtrusionPiece[] {
  const framingExtrusions: FloorFramingExtrusionPiece[] = [];

  const extrudeFloorFramingType = (framingEntry: Framing, joistPlacement: JoistPlacement) => {
    const { joists, extrusionFramingProfile } = framingEntry;
    const { profileDescriptor, railsByType } = joists;
    const profile = extrusionFramingProfile
      ? [makeFramingProfile2D(extrusionFramingProfile)]
      : [makeLumberProfile(profileDescriptor)];

    const currRails = railsByType[joistPlacement];
    if (!currRails) {
      return;
    }

    for (let i = 0, l = currRails.length; i < l; i++) {
      const { rail, nX, nY, nZ } = currRails[i];
      framingExtrusions.push({
        brep: extrudeFaceAlongPolyLine3(profile, rail, nX, nY, nZ),
        joistPlacement,
        rail: currRails[i],
      });
    }
  };

  for (let i = 0; i < framing.length; i++) {
    const framingEntry = framing[i];
    extrudeFloorFramingType(framingEntry, JoistPlacement.RIM);
    extrudeFloorFramingType(framingEntry, JoistPlacement.FLOOR);
    extrudeFloorFramingType(framingEntry, JoistPlacement.NOGGIN);
  }

  return framingExtrusions;
}

/**
 * Extrude legacy framing format (used by the old floor system pipeline in floor.ts).
 * The new framed floor assembly pipeline (createFramedFloorAssembly) extrudes per-member
 * inline in floorFramingGeometry.ts with per-type color support.
 * @deprecated - on the path to removal
 */
export function extrudeFraming(framing: Framing[]): BRep[] {
  return extrudeFloorFramingPlacements(framing).map((piece) => piece.brep);
}

export function roofFramingFaces(framing: RoofFramingComponent[]): Face[] {
  const allFaces: Face[] = [];

  const generateFramingFacesByFloorFramingType = (pieces: LumberPiecesByProfile, type: string) => {
    const { profileDescriptor, railsByType } = pieces;
    const profile = [makeLumberProfile(profileDescriptor)];

    const currRails = railsByType[type];
    if (!currRails) {
      return;
    }

    for (let i = 0, l = currRails.length; i < l; i++) {
      const { rail, nX, nY, nZ, clipPlanes, startExtension, endExtension } = currRails[i];
      let railExt = polyLine3Copy(rail);
      if (startExtension && endExtension) {
        railExt = polyLine3ExtendBothEnds(railExt, startExtension, endExtension);
      } else if (startExtension) {
        railExt = polyLine3ExtendStart(railExt, startExtension);
      } else if (endExtension) {
        railExt = polyLine3ExtendEnd(railExt, endExtension);
      }
      let faces = extrudeFaceAlongPolyLine3(profile, railExt, nX, nY, nZ).faces;

      if (clipPlanes && clipPlanes.length > 0) {
        faces = clipFacesWithPlanes({
          faces,
          planes: [...clipPlanes],
          addCapFaces: true,
        });
      }
      allFaces.push(...faces);
    }
  };

  for (let i = 0; i < framing.length; i++) {
    const { lumber } = framing[i];
    generateFramingFacesByFloorFramingType(lumber, RoofFramingComponentType.TOP_CHORD);
    generateFramingFacesByFloorFramingType(lumber, RoofFramingComponentType.BOTTOM_CHORD);
    generateFramingFacesByFloorFramingType(lumber, RoofFramingComponentType.WEB);
    generateFramingFacesByFloorFramingType(lumber, RoofFramingComponentType.LOOKOUT);
    generateFramingFacesByFloorFramingType(lumber, RoofFramingComponentType.RIDGE);
    generateFramingFacesByFloorFramingType(lumber, RoofFramingComponentType.JACK);
  }

  return allFaces;
}
